<template>
  <ul id="system-list">
    <li v-for="component in components" :key="component.id">
      <h4>{{ component.name }}</h4>
      <div class="status-bar">
        <div
          :class="`component-bar-icon ${componentClass(component)}`"
          :title="componentTitle(component)"
        />
        <a
          href="https://qvalent.statuspage.io/"
          :class="`component-bar ${componentClass(component)}`"
          target="_blank"
          rel="noopener noreferrer"
          :title="componentTitle(component)"
        />
      </div>
    </li>
  </ul>
</template>

<script>
export default {
  props: {
    components: {
      required: true,
      type: Array
    }
  },
  methods: {
    convertToTitleCase(string) {
      const words = string.replace(/_/g, ' ').toLowerCase().split(' ');
      for (let i = 0; i < words.length; i += 1) {
        words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
      }
      return words.join(' ');
    },
    /**
     * Component statuses (see https://help.statuspage.io/help/overview-1):
     *   - operational
     *   - under_maintenance
     *   - degraded_performance
     *   - partial_outage
     *   - major_outage
     */
    componentClass(component) {
      return component.status.replace(/_/g, '-');
    },
    componentTitle(component) {
      return this.convertToTitleCase(component.status);
    }
  }
};
</script>

<style lang="scss">
/* stylelint-disable-next-line max-line-length */
// These colors are grabbed from an image in https://help.statuspage.io/help/component-status-incident-impact-and-top-level-status-calculations
// We're darkening some of them here to adjust for the dark background and so we can
// keep the original colors here to tweak them more easily
$operational-color: scale-color(#6cc365, $lightness: -15%);
$under-maintenance-color: #4c9bd9;
$degraded-performance-color: #edc133;
$partial-outage-color: #d9823a;
$major-outage-color: scale-color(#d65c4b, $lightness: -5%);
$unknown-status-color: #959595;

@media only screen and (max-width: 800px) {
  #system-list {
    margin-left: auto;
    margin-right: auto;
    width: 180px;

    .component-bar {
      display: none;
    }
  }
}

@function status-gradient($base-color) {
  $stripe-width: 25%;

  @return repeating-linear-gradient(
    -90deg,
    $base-color,
    scale-color($base-color, $lightness: -10%) $stripe-width,
    $base-color $stripe-width * 2
  );
}

// These are intentionally not base 64 for readability, inserting colours more easily
// and to be smaller file size wise
@function circle-svg($color) {
  @return url("data:image/svg+xml,%3csvg version='1.0' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' width='50px' height='50px' viewBox='0 0 50 50' enable-background='new 0 0 50 50' xml:space='preserve'%3e%3ccircle fill='#{escape-color($color)}' cx='25' cy='25' r='15.559'/%3e%3c/svg%3e");
}

@function cross-svg($color) {
  @return url("data:image/svg+xml,%3Csvg version='1.2' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' width='50px' height='50px' viewBox='0 0 50 50' xml:space='preserve'%3E%3Cline fill='none' stroke='#{escape-color($color)}' stroke-width='9' stroke-miterlimit='10' x1='6.098' y1='5.697' x2='44.838' y2='44.438'/%3E%3Cline fill='none' stroke='#{escape-color($color)}' stroke-width='9' stroke-miterlimit='10' x1='44.838' y1='5.697' x2='6.098' y2='44.438'/%3E%3C/svg%3E");
}

@function tick-svg($color) {
  @return url("data:image/svg+xml,%3Csvg version='1.2' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' width='50px' height='50px' viewBox='0 0 50 50' xml:space='preserve'%3E%3Cpolyline fill='none' stroke='#{escape-color($color)}' stroke-width='9' stroke-miterlimit='10' points='7.361,24.387 18.913,35.939 44.557,10.296'/%3E%3C/svg%3E%0A");
}

#system-list {
  li {
    display: flex;
    margin: 55px 0;
    width: 100%;

    &:last-child {
      margin-bottom: 0;
    }
  }

  h4 {
    min-width: 140px;
    width: 20%;
  }
}

.status-bar {
  display: flex;
  width: 100%;
}

.component-bar {
  // This background is only used if we get an unknown status (shouldn't occur)
  background: status-gradient($unknown-status-color);
  border-radius: 3px;
  display: inline-block;
  height: 25px;
  vertical-align: top;
  width: 100%;

  &.operational {
    background: status-gradient($operational-color);
  }

  &.under-maintenance {
    background: status-gradient($under-maintenance-color);
  }

  &.degraded-performance {
    background: status-gradient($degraded-performance-color);
  }

  &.partial-outage {
    background: status-gradient($partial-outage-color);
  }

  &.major-outage {
    background: status-gradient($major-outage-color);
  }
}

.component-bar-icon {
  // This background is only used if we get an unknown status (shouldn't occur)
  background: circle-svg($unknown-status-color);
  background-size: 25px 25px;
  display: inline-block;
  height: 26px;
  margin-right: 12px;
  width: 26px;

  &.operational {
    background: tick-svg($operational-color);
    background-size: 25px 25px;
  }

  &.under-maintenance {
    background: circle-svg($under-maintenance-color);
    background-size: 25px 25px;
  }

  &.degraded-performance {
    background: circle-svg($degraded-performance-color);
    background-size: 25px 25px;
  }

  &.partial-outage {
    background: circle-svg($partial-outage-color);
    background-size: 25px 25px;
  }

  &.major-outage {
    background: cross-svg($major-outage-color);
    background-size: 25px 25px;
  }
}
</style>
